import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart, ArrowLeft, ShoppingBag } from 'lucide-react';
import Card from '../modern-components/ui/Card';
import Button from '../modern-components/ui/Button';
import Badge from '../modern-components/ui/Badge';
import { useWishlist, useCart } from '../context';

const Wishlist = () => {
  const navigate = useNavigate();
  const { wishlistItems, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart, isInCart } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getProductImage = (product) => {
    if (product.image_url) {
      if (product.image_url.startsWith('http')) {
        return product.image_url;
      }
      return `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000'}${product.image_url}`;
    }
    return 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=600';
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  const handleMoveAllToCart = () => {
    wishlistItems.forEach(item => {
      if (!isInCart(item.id)) {
        addToCart(item, 1);
      }
    });
  };

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16 animate-fade-in">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Wishlist is Empty</h2>
            <p className="text-gray-600 mb-8">
              Save your favorite products here to buy them later.
            </p>
            <Link to="/products">
              <Button variant="primary" size="lg" leftIcon={<ShoppingBag className="w-5 h-5" />}>
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate('/products')}
          className="mb-6 animate-fade-in"
        >
          Continue Shopping
        </Button>

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Wishlist</h1>
            <p className="text-lg text-gray-600">
              {wishlistItems.length} item{wishlistItems.length !== 1 && 's'} saved
            </p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button
              variant="primary"
              leftIcon={<ShoppingCart className="w-5 h-5" />}
              onClick={handleMoveAllToCart}
            >
              Add All to Cart
            </Button>
            <Button
              variant="outline"
              leftIcon={<Trash2 className="w-5 h-5" />}
              onClick={clearWishlist}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Clear Wishlist
            </Button>
          </div>
        </div>

        {/* Wishlist Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {wishlistItems.map((item) => (
            <Card key={item.id} hover padding="none" className="group overflow-hidden animate-slide-up">
              <div className="relative">
                {/* Product Image */}
                <Link to={`/products/${item.id}`}>
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={getProductImage(item)}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {item.is_active ? (
                      <Badge variant="success" size="sm" className="absolute top-3 left-3">
                        Available
                      </Badge>
                    ) : (
                      <Badge variant="danger" size="sm" className="absolute top-3 left-3">
                        Unavailable
                      </Badge>
                    )}
                  </div>
                </Link>

                {/* Remove Button */}
                <button
                  onClick={() => removeFromWishlist(item.id)}
                  className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove from wishlist"
                >
                  <Heart className="w-5 h-5 fill-current" />
                </button>
              </div>

              <div className="p-4">
                {/* Category */}
                <Badge variant="gray" size="sm" className="mb-2">
                  {item.category?.name || 'Uncategorized'}
                </Badge>

                {/* Product Name */}
                <Link to={`/products/${item.id}`}>
                  <h3 className="font-semibold text-gray-900 hover:text-primary-600 transition-colors mb-1">
                    {item.name}
                  </h3>
                </Link>

                {/* Description */}
                <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                  {item.description}
                </p>

                {/* Price */}
                <div className="mb-4">
                  {item.price_range_min && item.price_range_max ? (
                    <>
                      <div className="font-bold text-gray-900">
                        {formatPrice(item.price_range_min)} - {formatPrice(item.price_range_max)}
                      </div>
                      <div className="text-xs text-gray-500">per {item.unit || 'unit'}</div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">Price on request</div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant={isInCart(item.id) ? 'outline' : 'primary'}
                    size="sm"
                    className="flex-1"
                    leftIcon={<ShoppingCart className="w-4 h-4" />}
                    onClick={() => handleAddToCart(item)}
                    disabled={!item.is_active || isInCart(item.id)}
                  >
                    {isInCart(item.id) ? 'In Cart' : 'Add to Cart'}
                  </Button>
                  <Link to={`/get-quote?product=${item.id}`}>
                    <Button variant="outline" size="sm" disabled={!item.is_active}>
                      Get Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
